/* eslint-disable import/no-cycle */

import {
  ApolloCache,
  ApolloQueryResult,
  DefaultContext,
  FetchMoreQueryOptions,
  FetchResult,
  makeVar,
  MutationFunctionOptions
} from '@apollo/client';
import dayjs from 'dayjs';
import { NavigateFunction } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SweetAlertResult } from 'sweetalert2';

import { GET_PAGINATED_TASKS, GET_TASK_STATISTICS } from '@api/operations/queries/Tasks.gql';
import { GetPaginatedTasksVariables } from '@api/operations/queries/types/GetPaginatedTasks';
import {
  DeleteTaskMutation,
  DeleteTaskMutationVariables,
  GetPaginatedTasksQuery,
  GetPaginatedTasksQueryVariables,
  RequestPriority,
  TaskLabels,
  TaskStatus,
  UpdateTaskMutation,
  UpdateTaskMutationVariables
} from '@src/api/gql/graphql';
import { SelectOption } from '@src/app/modules/components/forms/types';
import ConfirmDangerousActionSwal from '@src/app/modules/components/modals/ConfirmDangerousActionSwal';
import {
  formatEnum,
  getStorageItem,
  sortByKey,
  uppercaseFirstLetter,
  upperEveryFirstLetter
} from '@src/utils/helperFunctions';
import { OperatorSearch, RecordSearch } from '@src/utils/types';
import { cache } from '@store/apollo/cache';
import {
  RequireOnlyOne,
  ServerPagination,
  SortByOrder,
  SortDirection,
  UserAlertsVar,
  UserTaskType,
  ValuesUnion
} from '@store/types';

/* eslint-disable max-len */
export enum TaskActions {
  START = 1,
  STOP,
  RESUME,
  CANCEL
}

export enum TaskTags {
  LOW = 'Low',
  NORMAL = 'Normal',
  URGENT = 'Urgent',
  TEAM = 'Team'
}

enum NonInclusiveTaskLabels {
  DELETED = TaskLabels.Deleted,
  COMPLETED = TaskLabels.Completed,
  NEW = TaskLabels.New,
  CANCELLED = TaskLabels.Cancelled,
  ACTIVE = TaskLabels.Active
}

type TaskTarget = RequireOnlyOne<{ taskId: string, task: UserTaskType }>;

type TaskDispatchParams = {

  query?: string
  sort?: {
    attribute: string,
    direction: SortDirection
  }
};

type TaskDispatchFilter = {
  label?: TaskLabels
  tag?: TaskTags
};

export type TaskDispatchState = {
  tasks: UserTaskType[]
  selectedTask: UserTaskType,
  pagination: ServerPagination
  params?: TaskDispatchParams
  filter?: TaskDispatchFilter
  currentQueryVariables?: GetPaginatedTasksQueryVariables
};

export type DispatchTaskUpdate = (options?: MutationFunctionOptions<UpdateTaskMutation, UpdateTaskMutationVariables, DefaultContext, ApolloCache<any>>) => Promise<FetchResult<UpdateTaskMutation, Record<string, any>, Record<string, any>>>;
export type DispatchTaskDelete = (options?: MutationFunctionOptions<DeleteTaskMutation, DeleteTaskMutationVariables, DefaultContext, ApolloCache<any>>) => Promise<FetchResult<DeleteTaskMutation, Record<string, any>, Record<string, any>>>;

export type DispatchTaskFetchMore = <TFetchData = GetPaginatedTasksQuery, TFetchVars = GetPaginatedTasksQueryVariables>(fetchMoreOptions: FetchMoreQueryOptions<TFetchVars, TFetchData>) => Promise<ApolloQueryResult<TFetchData>>;

export const taskStateVar = makeVar<TaskDispatchState>({
  tasks: [],
  selectedTask: undefined,
  params: {
    sort: { attribute: 'created_at', direction: 'DESC' }
  },
  filter: {
    label: TaskLabels.Default
  },
  pagination: { page: 0, size: 15 },
  currentQueryVariables: undefined
});

export const userAlertsCache = new Map<keyof UserAlertsVar, Partial<UserAlertsVar>>();

export const getInitialAlerts = () => {
  const defaultValues: UserAlertsVar = {
    scheduleAlerts: {
      count: 0
    },
    taskAlert: {
      ignored: false,
      shown: false
    }
  };

  return getStorageItem('userAlerts') ?? defaultValues;
};

export const pendingTaskStatuses = [TaskStatus.Accepted, TaskStatus.Requested, TaskStatus.Received];
const openTaskStatuses = [
  TaskStatus.OnHold,
  TaskStatus.InProgress,
  TaskStatus.Accepted,
  TaskStatus.Rejected
];
export const concludingTaskStatuses = [
  TaskStatus.Completed, TaskStatus.Rejected,
  TaskStatus.Failed,
  TaskStatus.Cancelled
];
export const actionTaskStatusMap = {
  [TaskStatus.Received]: {
    color: 'flat-info',
    icon: 'Inbox',
    iconColor: 'text-primary'
  },
  [TaskStatus.Completed]: {
    color: 'none',
    icon: 'CheckCircle',
    iconColor: 'text-success'
  },
  [TaskStatus.InProgress]: {
    color: 'primary',
    icon: 'PlayCircle',
    iconColor: 'white'
  },
  [TaskStatus.Failed]: {
    color: 'none',
    icon: 'X',
    iconColor: 'text-danger'
  },
  [TaskStatus.Cancelled]: {
    color: 'none',
    icon: 'XCircle',
    iconColor: 'text-danger'
  },
  [TaskStatus.OnHold]: {
    color: 'warning',
    icon: 'Pause',
    iconColor: 'text-warning'
  }
};
export const displayTaskStatusMap = {
  [TaskStatus.Received]: {
    color: 'flat-info',
    icon: 'Inbox',
    iconColor: 'text-primary'
  },
  [TaskStatus.InProgress]: {
    color: 'flat-primary',
    icon: 'RefreshCw',
    iconColor: 'text-info'
  },
  [TaskStatus.Completed]: {
    color: 'none',
    icon: 'CheckCircle',
    iconColor: 'text-success'
  },
  [TaskStatus.Cancelled]: {
    color: 'none',
    icon: 'XCircle',
    iconColor: 'text-danger'
  },
  [TaskStatus.OnHold]: {
    color: 'warning',
    icon: 'Pause',
    iconColor: 'text-warning'
  }
};

export const TaskTagPriorityMapping: Record<ValuesUnion<RequestPriority>, TaskTags> = {
  [RequestPriority.Low]: TaskTags.LOW,
  [RequestPriority.Routine]: TaskTags.NORMAL,
  [RequestPriority.Urgent]: TaskTags.URGENT,
  [RequestPriority.Stat]: TaskTags.TEAM,
  [RequestPriority.Asap]: TaskTags.URGENT
};

export const TaskActionStatusMap = {
  [TaskActions.START]: TaskStatus.InProgress,
  [TaskActions.STOP]: TaskStatus.OnHold,
  [TaskActions.RESUME]: TaskStatus.InProgress,
  [TaskActions.CANCEL]: TaskStatus.Cancelled
};

export const TagColorMap: Record<ValuesUnion<TaskTags>, string> = {
  [TaskTags.LOW]: 'light-primary',
  [TaskTags.NORMAL]: 'light-warning',
  [TaskTags.URGENT]: 'light-danger',
  [TaskTags.TEAM]: 'light-info'
};

export const TaskLabelMapping: Record<ValuesUnion<NonInclusiveTaskLabels>, string> = {
  [TaskLabels.Deleted]: 'deleted_at',
  [TaskLabels.Completed]: 'completed_at',
  [TaskLabels.New]: JSON.stringify(pendingTaskStatuses.map((status) => formatEnum(status))),
  [TaskLabels.Cancelled]: JSON.stringify([formatEnum(TaskStatus.Cancelled)]),
  [TaskLabels.Active]: JSON.stringify(openTaskStatuses.map((status) => upperEveryFirstLetter(formatEnum(status))))
};

export const TaskTagSelectOptions: Array<SelectOption<RequestPriority>> = [
  { value: RequestPriority.Low, label: TaskTags.LOW },
  { value: RequestPriority.Routine, label: TaskTags.NORMAL },
  { value: RequestPriority.Asap, label: TaskTags.URGENT },
  { value: RequestPriority.Stat, label: TaskTags.TEAM }
];

const getTaskSelection = (state: TaskDispatchState, target: TaskTarget) => {
  let selectedTask: UserTaskType | undefined;

  if (target.taskId) {
    const foundTask = state.tasks.find((task) => task.task_id === target.taskId);

    if (!foundTask) {
      if (process.env.REACT_APP_MODE === 'DEVELOPMENT') {
        throw new TypeError('Development Error: Task marked for selection is undefined.');
      }
    }

    selectedTask = foundTask;
  } else {
    selectedTask = target.task;
  }

  return selectedTask;
};

const TaskDispatcher = {

  GetCachedTasks: (state: TaskDispatchState, variables?: GetPaginatedTasksVariables) => {
    const query = TaskDispatcher.GetSearchVariables(state);
    const vars: GetPaginatedTasksVariables = !variables ? {
      pagination: {
        page: state.pagination.page,
        size: state.pagination.size
      },
      searchBy: {
        ...(Boolean(query?.querySet) && {
          querySet: [query.querySet]
        }),
        ...(Boolean(query?.queryOperator) && {
          queryOperator: [query.queryOperator]
        }),
        queryKeyword: [{ columnName: 'Tasks.title', keyword: state.params?.query ?? '' }]
      },
      options: {
        paranoid: !query?.queryOperator?.columnName.includes('deleted_at')
      }
    } : variables;

    const cachedTasks: GetPaginatedTasksQuery | [] = cache.readQuery({
      query: GET_PAGINATED_TASKS,
      variables: vars
    }) ?? [];

    const tasks = Array.isArray(cachedTasks) ? [] : cachedTasks.getPaginatedTasks.rows;
    // const pagination = isArrayLike(cachedTasks) ? state.pagination : { ...state.pagination, totalPages: cachedTasks.getPaginatedTasks.totalPages, currentPage: cachedTasks.getPaginatedTasks.currentPage };

    return { ...state, tasks };
  },

  SelectTask: (state: TaskDispatchState, target?: TaskTarget) => {
    const selectedTask = target && getTaskSelection(state, target);

    return taskStateVar({ ...state, selectedTask });
  },
  LoadMore: async (state: TaskDispatchState, dispatcher: DispatchTaskFetchMore) => {
    if (state.pagination.currentPage >= state.pagination.totalPages) return;
    const newPage = state.pagination.page + 1;
    const request = await dispatcher({
      variables: {
        pagination: {
          page: newPage,
          size: state.pagination.size
        }
      }
    });

    if (!request.error) {
      // eslint-disable-next-line no-param-reassign
      state.pagination = { ...state.pagination, page: newPage };
    }
  },
  UpdateLabelFilter: (state: TaskDispatchState, label: TaskLabels) => taskStateVar({
    ...state,
    pagination: { ...state.pagination, page: 0, currentPage: 0 },
    filter: { ...state.filter, label }
  }),

  UpdateTagFilter: (state: TaskDispatchState, tag: TaskTags) => taskStateVar({
    ...state, filter: { ...state.filter, tag }
  }),

  GetSearchVariables: (state: TaskDispatchState) => {
    const nonInclusiveLabels = [TaskLabels.Deleted, TaskLabels.Completed];
    const statusRelatedLabels = [TaskLabels.New, TaskLabels.Active, TaskLabels.Cancelled];

    const { label } = state.filter;
    if (label === TaskLabels.Default) return undefined;

    let querySet: RecordSearch = {
      columnName: 'label',
      stringValues: [uppercaseFirstLetter(state.filter.label.toLowerCase())]
    };

    let queryOperator: OperatorSearch = {
      operator: 'not',
      columnName: '',
      value: 'null'
    };

    if (nonInclusiveLabels.includes(label) || statusRelatedLabels.includes(label)) {
      querySet = undefined;

      if (nonInclusiveLabels.includes(label)) {
        queryOperator.columnName = JSON.stringify(TaskLabelMapping[label]);
      }

      if (statusRelatedLabels.includes(label)) {
        queryOperator = {
          columnName: 'status',
          operator: 'in',
          value: TaskLabelMapping[label]
        };
      }
    } else {
      queryOperator = undefined;
    }

    return { querySet, queryOperator };
  },

  ReorderTasks: (state: TaskDispatchState, tasks: UserTaskType[]) => taskStateVar({
    ...state,
    tasks
  }),

  SetSearchQuery: (state: TaskDispatchState, query: string) => taskStateVar({
    ...state,
    params: { ...state.params, query }
  }),

  SetSort: (state: TaskDispatchState, attribute: string, direction?: SortDirection) => {
    const DEFAULT_ATTRIBUTE = 'created_at';
    let sortDirection = direction;

    const getSortDirection = () => {
      if (!state.params.sort) {
        return 'DESC';
      }

      const { sort } = state.params;
      if (sort.attribute.trim().toLowerCase() === attribute.trim().toLowerCase()) {
        return sort.direction === 'ASC' ? 'DESC' : 'ASC';
      }
      return 'ASC';
    };

    if (attribute === DEFAULT_ATTRIBUTE) {
      sortDirection = direction ?? getSortDirection();
    }

    if (attribute !== DEFAULT_ATTRIBUTE) {
      sortDirection = getSortDirection();
    }

    const sorted = sortByKey(state.tasks, attribute, SortByOrder[sortDirection.toUpperCase()] ?? 1);
    return taskStateVar({
      ...state,
      tasks: sorted,
      params: { query: state.params.query, sort: { attribute, direction: sortDirection } }
    });
  },

  SetImportant: async (
    state: TaskDispatchState,
    dispatcher: DispatchTaskUpdate,
    target: TaskTarget
  ) => {
    const selectedTask = getTaskSelection(state, target)!;
    const label = selectedTask.label === 'IMPORTANT' ? TaskLabels.Default : TaskLabels.Important;
    const clonedTask = structuredClone(selectedTask);

    Object.assign(clonedTask, { label });

    taskStateVar({ ...state, selectedTask: clonedTask });

    await dispatcher({
      variables: {
        payload: { taskId: selectedTask.task_id, label }
      },
      // refetchQueries: [GET_PAGINATED_TASKS],
      onError: (err) => toast.error(err.message)
    });

    // eslint-disable-next-line no-param-reassign
    state.pagination = { ...state.pagination, page: 0 };
    return TaskDispatcher.GetCachedTasks(state);
  },
  StartTask: async (
    state: TaskDispatchState,
    dispatcher: DispatchTaskUpdate,
    navigate: NavigateFunction,
    target: TaskTarget
  ) => {
    const selectedTask = getTaskSelection(state, target)!;
    if (selectedTask.status !== TaskActionStatusMap[TaskActions.START]) {
      await dispatcher({
        variables: {
          payload: {
            taskId: selectedTask.task_id,
            status: TaskActionStatusMap[TaskActions.START],
            startedAt: dayjs().toISOString()
          }
        },
        onError: (err) => toast.error(err.message)
      });
    }
    TaskDispatcher.GetCachedTasks(state);
    return selectedTask.link && navigate(`/${selectedTask.link}`, { replace: true });
  },
  ResumeTask: async (
    state: TaskDispatchState,
    dispatcher: DispatchTaskUpdate,
    navigate: NavigateFunction,
    target: TaskTarget
  ) => {
    const selectedTask = getTaskSelection(state, target)!;
    if (selectedTask.status !== TaskActionStatusMap[TaskActions.RESUME]) {
      await dispatcher({
        variables: {
          payload: {
            taskId: selectedTask.task_id,
            status: TaskActionStatusMap[TaskActions.RESUME]
          }
        },
        onError: (err) => toast.error(err.message)
      });
    }
    TaskDispatcher.GetCachedTasks(state);

    return selectedTask.link && navigate(`/${selectedTask.link}`, { replace: true });
  },

  CancelTask: async (
    state: TaskDispatchState,
    dispatcher: DispatchTaskUpdate,
    target: TaskTarget
  ) => {
    const handleCancel = async (
      onConfirmed: (result: SweetAlertResult) => Promise<void> | void
    ) => ConfirmDangerousActionSwal(
      {
        title: 'Are you sure you want to cancel this task?',
        confirmButtonText: 'Yes',
        // TODO:[V2] Revisit this. Idea was to gather additional information into why the task is being cancelled
        // if (res.isConfirmed){
        //   return cancelTaskModal().then(onConfirmed);
        // }
        onConfirmed: async (res) => onConfirmed(res)

      }
    );
    let cancelConfirmed = false;
    await handleCancel((res) => {
      cancelConfirmed = res.isConfirmed;
    });
    if (!cancelConfirmed) return null;

    const selectedTask = getTaskSelection(state, target)!;

    const executor = dispatcher({
      variables: {
        payload: {
          taskId: selectedTask.task_id,
          status: TaskActionStatusMap[TaskActions.CANCEL]
        }
      }
    });

    await toast.promise(executor, {
      pending: 'Cancelling task..',
      success: 'Task cancelled!',
      error: {
        render(error) {
          return String(error.data);
        }
      }
    });

    return TaskDispatcher.GetCachedTasks(state);
  },

  DeleteTask: async (state: TaskDispatchState, dispatcher: DispatchTaskDelete, taskId: string) => {
    const executor = dispatcher({
      refetchQueries: [GET_TASK_STATISTICS],
      variables: {
        // `deleteTask` now takes `TaskDeleteInput!` (`{ taskId }`) rather
        // than a flat string. Keep accepting the bare taskId at the call
        // site for ergonomics — wrap it here.
        payload: { taskId }
      }
    });
    await toast.promise(executor, {
      pending: 'Deleting task..',
      success: 'Task deleted!',
      error: {
        render(error) {
          return String(error.data);
          // return <p>{String(error.data)}</p>;
        }
      }
    });

    return TaskDispatcher.GetCachedTasks(state);
  }
};

export default TaskDispatcher;
